import { Box, Typography } from '@mui/material';
import { FC, ReactNode } from 'react';
import type { StageSummary } from './types';

// Eigene, helle Farbwelt der Partnerseite — unabhaengig vom internen Dashboard-Theme (dessen Grautoene
// sind im hellen Modus invertiert, theme.ts).
export const C = {
  bg: '#f4f6f9',
  card: '#ffffff',
  line: '#e4e8ee',
  ink: '#0f1728',
  ink2: '#475467',
  ink3: '#8a94a6',
  brand: '#0b4f8a',
  brandSoft: '#e8f1fa',
  prio1: '#0b4f8a',
  prio2: '#5aa9e6',
  billable: '#12a150',
  unsub: '#e5484d',
  bounce: '#f08c00',
  other: '#a0a8b8',
  checkRunning: '#3b82f6',
  resultPending: '#8b5cf6',
  waitingFirstMail: '#cfd6e0',
};

/** Schrift der Partnerseite; im Cockpit-Spiegel ist Source Sans nicht geladen, dann greift die Systemschrift. */
export const FONT = "'Source Sans 3', Roboto, system-ui, -apple-system, 'Segoe UI', Arial, sans-serif";

export const STAGES: Array<{ key: keyof StageSummary; label: string; color: string; hint: string; final: boolean }> = [
  { key: 'billable', label: 'Abrechenbar', color: C.billable, hint: '48 h nach der ersten MSM-Mail noch angemeldet', final: true },
  { key: 'unsub', label: 'Abgemeldet', color: C.unsub, hint: 'innerhalb von 48 h nach der ersten MSM-Mail abgemeldet', final: true },
  { key: 'bounce', label: 'Unzustellbar', color: C.bounce, hint: 'von Mailchimp als unzustellbar entfernt', final: true },
  { key: 'other', label: 'Nicht angemeldet (sonstiger Status)', color: C.other, hint: 'zum Prüfzeitpunkt archiviert, gelöscht oder in anderem Status', final: true },
  { key: 'checkRunning', label: 'Prüffrist läuft', color: C.checkRunning, hint: 'erste MSM-Mail erhalten, die 48 h laufen noch', final: false },
  { key: 'resultPending', label: 'Ergebnis ausstehend', color: C.resultPending, hint: '48 h vorbei, Ergebnis wird beim nächsten Abgleich festgeschrieben', final: false },
  { key: 'waitingFirstMail', label: 'Wartet auf erste MSM-Mail', color: C.waitingFirstMail, hint: 'geliefert, aber noch in keinem MSM-Versand', final: false },
];

const nf = new Intl.NumberFormat('de-DE');
export const int = (n: number | null | undefined) => (n === null || n === undefined ? '–' : nf.format(n));
export const eur = (n: number) =>
  new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n);
export const pct = (a: number | null | undefined, b: number | null | undefined, digits = 1) =>
  a === null || a === undefined || !b ? '–' : `${new Intl.NumberFormat('de-DE', { minimumFractionDigits: digits, maximumFractionDigits: digits }).format((100 * a) / b)} %`;

/** UTC-Zeitstempel 'YYYY-MM-DD HH:mm:ss' → Berliner Anzeige. */
export function berlin(utc: string | null | undefined, withTime = true): string {
  if (!utc) return '–';
  const d = new Date(`${utc.replace(' ', 'T')}Z`);
  return new Intl.DateTimeFormat('de-DE', {
    timeZone: 'Europe/Berlin',
    weekday: withTime ? 'short' : undefined,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    ...(withTime ? { hour: '2-digit', minute: '2-digit' } : {}),
  }).format(d);
}

export const monthLabel = (ym: string) => {
  const [y, m] = ym.split('-').map(Number);
  return new Intl.DateTimeFormat('de-DE', { month: 'long', year: 'numeric' }).format(new Date(Date.UTC(y, m - 1, 15)));
};

export const Card: FC<{ title?: string; subtitle?: ReactNode; action?: ReactNode; children: ReactNode }> = ({
  title,
  subtitle,
  action,
  children,
}) => (
  <Box
    sx={{
      bgcolor: C.card,
      border: `1px solid ${C.line}`,
      borderRadius: '14px',
      boxShadow: '0 1px 2px rgba(16,24,40,0.04)',
      p: { xs: 2, md: 3 },
      minWidth: 0,
    }}
  >
    {(title || action) && (
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Box sx={{ minWidth: 0 }}>
          {title && (
            <Typography sx={{ fontSize: 17, fontWeight: 700, color: C.ink, lineHeight: 1.3 }}>{title}</Typography>
          )}
          {subtitle && <Typography sx={{ fontSize: 13, color: C.ink2, mt: 0.5 }}>{subtitle}</Typography>}
        </Box>
        {action}
      </Box>
    )}
    {children}
  </Box>
);

export const Tile: FC<{ label: string; value: string; sub?: ReactNode; accent?: string }> = ({ label, value, sub, accent }) => (
  <Box
    sx={{
      bgcolor: C.card,
      border: `1px solid ${C.line}`,
      borderRadius: '14px',
      p: { xs: 2, md: 2.5 },
      position: 'relative',
      overflow: 'hidden',
      minWidth: 0,
    }}
  >
    <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, bgcolor: accent ?? C.brand }} />
    <Typography sx={{ fontSize: 13, color: C.ink2, fontWeight: 600 }}>{label}</Typography>
    <Typography sx={{ fontSize: { xs: 26, md: 30 }, fontWeight: 700, color: C.ink, lineHeight: 1.2, mt: 0.5 }}>{value}</Typography>
    {sub && <Typography component="div" sx={{ fontSize: 13, color: C.ink2, mt: 0.75 }}>{sub}</Typography>}
  </Box>
);

/** Gestapelter Balken der Ergebnisarten mit Legende. */
export const StageBar: FC<{ s: StageSummary; height?: number; legend?: boolean }> = ({ s, height = 14, legend = true }) => {
  const total = s.delivered || 1;
  return (
    <Box>
      <Box sx={{ display: 'flex', height, borderRadius: height, overflow: 'hidden', bgcolor: C.line }}>
        {STAGES.map((st) => {
          const v = s[st.key] as number;
          return v > 0 ? <Box key={st.key} title={`${st.label}: ${int(v)}`} sx={{ width: `${(100 * v) / total}%`, bgcolor: st.color }} /> : null;
        })}
      </Box>
      {legend && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(4, 1fr)' }, gap: 1.25, mt: 2 }}>
          {STAGES.map((st) => (
            <Box key={st.key} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', minWidth: 0 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: '3px', bgcolor: st.color, mt: '5px', flexShrink: 0 }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 13, color: C.ink, fontWeight: 600 }}>
                  {st.label} <span style={{ color: C.ink2, fontWeight: 400 }}>· {int(s[st.key] as number)}</span>
                </Typography>
                <Typography sx={{ fontSize: 12, color: C.ink3, lineHeight: 1.35 }}>{st.hint}</Typography>
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export const Progress: FC<{ label: string; value: number; total: number; color: string }> = ({ label, value, total, color }) => {
  const p = total > 0 ? Math.min(100, (100 * value) / total) : 0;
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
        <Typography sx={{ fontSize: 14, fontWeight: 600, color: C.ink }}>{label}</Typography>
        <Typography sx={{ fontSize: 13, color: C.ink2 }}>
          <b style={{ color: C.ink }}>{int(value)}</b> von {int(total)} · {pct(value, total)}
        </Typography>
      </Box>
      <Box sx={{ height: 10, borderRadius: 10, bgcolor: C.line, mt: 1, overflow: 'hidden' }}>
        <Box sx={{ width: `${p}%`, height: '100%', bgcolor: color, borderRadius: 10 }} />
      </Box>
    </Box>
  );
};

export const Chip: FC<{ children: ReactNode; color: string; bg: string }> = ({ children, color, bg }) => (
  <Box component="span" sx={{ display: 'inline-block', px: 1, py: '2px', borderRadius: '999px', fontSize: 12, fontWeight: 600, color, bgcolor: bg, whiteSpace: 'nowrap' }}>
    {children}
  </Box>
);

/** Tabelle, die auf schmalen Bildschirmen IN der Karte scrollt — die Seite selbst scrollt nie seitlich. */
export const TableWrap: FC<{ children: ReactNode }> = ({ children }) => (
  <Box
    sx={{
      overflowX: 'auto',
      mx: { xs: -2, md: 0 },
      px: { xs: 2, md: 0 },
      '& table': { width: '100%', borderCollapse: 'collapse', fontSize: 14, color: C.ink },
      '& th': { textAlign: 'right', fontSize: 12, fontWeight: 600, color: C.ink2, py: 1, px: 1.25, borderBottom: `1px solid ${C.line}`, whiteSpace: 'nowrap' },
      '& td': { textAlign: 'right', py: 1.1, px: 1.25, borderBottom: `1px solid ${C.line}`, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' },
      '& th:first-of-type, & td:first-of-type': { textAlign: 'left', pl: 0 },
      '& tr:last-of-type td': { borderBottom: 'none' },
    }}
  >
    {children}
  </Box>
);
